// # Mentor Meeting Tools Component
import React, { useState } from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  TextField,
  CircularProgress,
  IconButton,
  Typography
} from '@mui/material';
import { LocalizationProvider, DatePicker, TimePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { FaEdit, FaTrash, FaCalendarAlt, FaClock, FaTimes, FaCheck } from 'react-icons/fa';
import { doc, updateDoc, deleteDoc, getDoc, setDoc } from 'firebase/firestore';
import { db, functions } from '../../../../firebase/config';
import { httpsCallable } from 'firebase/functions';
import './mentor_meet_tools.css';

// # Edit Meeting Dialog Component
export const EditMeetingDialog = ({ open, onClose, meeting, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // # Form state - initialize with meeting data
  const [formData, setFormData] = useState({
    title: meeting?.title || '',
    description: meeting?.description || '',
    selectedDate: meeting?.meet_time ? new Date(meeting.meet_time) : null,
    selectedTime: meeting?.meet_time ? new Date(meeting.meet_time) : null,
    meetingUrl: meeting?.host?.url || ''
  });

  // # Reset form when meeting changes
  React.useEffect(() => {
    if (meeting) {
      setFormData({
        title: meeting.title || '',
        description: meeting.description || '',
        selectedDate: meeting.meet_time ? new Date(meeting.meet_time) : null,
        selectedTime: meeting.meet_time ? new Date(meeting.meet_time) : null,
        meetingUrl: meeting.host?.url || ''
      });
    }
  }, [meeting]);

  // # Handle form changes
  const handleInputChange = (field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
    setError('');
  };

  // # Handle date/time picker changes
  const handleDateChange = (newDate) => {
    setFormData(prev => ({
      ...prev,
      selectedDate: newDate
    }));
    setError('');
  };

  const handleTimeChange = (newTime) => {
    setFormData(prev => ({
      ...prev,
      selectedTime: newTime
    }));
    setError('');
  };

  // # Combine date and time for meeting
  const combineDateAndTime = () => {
    if (!formData.selectedDate || !formData.selectedTime) return null;
    
    const date = new Date(formData.selectedDate);
    const time = new Date(formData.selectedTime);
    
    return new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      time.getHours(),
      time.getMinutes()
    );
  };

  // # Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // # Validate form
      const { title, description, selectedDate, selectedTime, meetingUrl } = formData;
      
      if (!title?.trim()) throw new Error('Please enter a meeting title');
      if (!description?.trim()) throw new Error('Please enter a meeting description');
      if (!selectedDate) throw new Error('Please select a meeting date');
      if (!selectedTime) throw new Error('Please select a meeting time');
      if (!meetingUrl?.trim()) throw new Error('Meeting URL is required');

      const meetTime = combineDateAndTime();
      if (!meetTime) throw new Error('Invalid date/time combination');

      // # Get the meeting document reference
      const meetingDocRef = doc(
        db, 
        'meetings', 
        meeting.type
      );
      
      // # First get the existing meeting data to preserve structure
      const meetingDoc = await getDoc(meetingDocRef);
      if (!meetingDoc.exists()) {
        throw new Error('Meeting document not found');
      }
      
      const existingData = meetingDoc.data();
      const existingMeeting = existingData[meeting.id];
      
      if (!existingMeeting) {
        throw new Error('Meeting data not found');
      }

      // # Update the meeting document, preserving the existing fields
      await updateDoc(meetingDocRef, {
        [meeting.id]: {
          ...existingMeeting,
          title: title.trim(),
          description: description.trim(),
          meet_time: meetTime,
          host: {
            ...existingMeeting.host,
            url: meetingUrl.trim()
          },
          updated_at: new Date().toISOString()
        }
      });

      // # Call success callback
      onSuccess && onSuccess();
      onClose();
    } catch (err) {
      console.error('Error updating meeting:', err);
      setError(err.message || 'An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  if (!open || !meeting) return null;

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="sm" 
      fullWidth
      className="mmtl_edit_dialog"
    >
      <DialogTitle className="mmtl_dialog_title">
        Edit Meeting
        <IconButton
          aria-label="close"
          onClick={onClose}
          className="mmtl_close_button"
        >
          <FaTimes />
        </IconButton>
      </DialogTitle>

      <DialogContent className="mmtl_dialog_content">
        <form onSubmit={handleSubmit} className="mmtl_form">
          {/* Title input */}
          <div className="mmtl_form_field">
            <label htmlFor="meeting-title">Title</label>
            <TextField
              id="meeting-title"
              value={formData.title}
              onChange={handleInputChange('title')}
              placeholder="Enter meeting title"
              fullWidth
              variant="outlined"
              size="small"
              required
            />
          </div>

          {/* Meeting URL input */}
          <div className="mmtl_form_field">
            <label htmlFor="meeting-url">Meeting URL</label>
            <TextField
              id="meeting-url"
              type="url"
              value={formData.meetingUrl}
              onChange={handleInputChange('meetingUrl')}
              placeholder="https://zoom.us/j/..."
              fullWidth
              variant="outlined"
              size="small"
              required
            />
          </div>

          {/* Date and time selection */}
          <div className="mmtl_datetime_container">
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <div className="mmtl_form_field">
                <label htmlFor="meeting-date">
                  <FaCalendarAlt className="mmtl_icon" /> Date
                </label>
                <DatePicker
                  value={formData.selectedDate}
                  onChange={handleDateChange}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      variant: "outlined",
                      error: !!error && !formData.selectedDate,
                      helperText: !!error && !formData.selectedDate ? 'Date is required' : '',
                      size: "small"
                    }
                  }}
                />
              </div>
              <div className="mmtl_form_field">
                <label htmlFor="meeting-time">
                  <FaClock className="mmtl_icon" /> Time
                </label>
                <TimePicker
                  value={formData.selectedTime}
                  onChange={handleTimeChange}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      variant: "outlined",
                      error: !!error && !formData.selectedTime,
                      helperText: !!error && !formData.selectedTime ? 'Time is required' : '',
                      size: "small"
                    }
                  }}
                />
              </div>
            </LocalizationProvider>
          </div>

          {/* Description textarea */}
          <div className="mmtl_form_field">
            <label htmlFor="meeting-description">Description</label>
            <TextField
              id="meeting-description"
              value={formData.description}
              onChange={handleInputChange('description')}
              placeholder="Enter meeting details"
              fullWidth
              multiline
              rows={2}
              variant="outlined"
              size="small"
              required
            />
          </div>

          {/* Error message */}
          {error && (
            <div className="mmtl_error">
              <FaTimes />
              <span>{error}</span>
            </div>
          )}
        </form>
      </DialogContent>

      <DialogActions className="mmtl_dialog_actions">
        <Button 
          onClick={onClose} 
          className="mmtl_cancel_button"
          disabled={loading}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          className="mmtl_save_button"
          disabled={loading}
        >
          {loading ? (
            <>
              <CircularProgress size={16} color="inherit" />
              <span>Saving...</span>
            </>
          ) : (
            <span>Save Changes</span>
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// # Delete Meeting Dialog Component
export const DeleteMeetingDialog = ({ open, onClose, meeting, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // # Handle delete meeting
  const handleDelete = async () => {
    setLoading(true);
    setError('');

    try {
      if (!meeting?.id || !meeting?.type) {
        throw new Error('Invalid meeting data');
      }

      // # Get the meeting document reference
      const meetingDocRef = doc(db, 'meetings', meeting.type);
      
      // # Check if the meeting exists
      const meetingDoc = await getDoc(meetingDocRef);
      if (!meetingDoc.exists()) {
        throw new Error('Meeting not found');
      }

      // # Complete removal using FieldValue.delete() instead of setting to null
      const existingData = meetingDoc.data();
      const updatedData = { ...existingData };
      
      // # Delete the field properly by removing it from the object
      delete updatedData[meeting.id];
      
      // # Update the document with the field completely removed
      await setDoc(meetingDocRef, updatedData);

      // # Call success callback
      onSuccess && onSuccess();
      onClose();
    } catch (err) {
      console.error('Error deleting meeting:', err);
      setError(err.message || 'An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  if (!open || !meeting) return null;

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="xs" 
      fullWidth
      className="mmtl_delete_dialog"
    >
      <DialogTitle className="mmtl_dialog_title mmtl_delete_title">
        Delete Meeting
        <IconButton
          aria-label="close"
          onClick={onClose}
          className="mmtl_close_button"
        >
          <FaTimes />
        </IconButton>
      </DialogTitle>

      <DialogContent className="mmtl_dialog_content">
        <div className="mmtl_delete_content">
          <Typography variant="h6" className="mmtl_delete_question">
            Are you sure you want to delete this meeting?
          </Typography>
          
          <div className="mmtl_meeting_details">
            <Typography variant="body1" className="mmtl_meeting_title">
              <strong>{meeting.title}</strong>
            </Typography>
            <Typography variant="body2" className="mmtl_meeting_datetime">
              <FaCalendarAlt className="mmtl_icon" />
              {meeting.meet_time 
                ? new Date(meeting.meet_time).toLocaleDateString('en-US', {
                    weekday: 'short',
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                  }) 
                : 'Unknown date'}
              <span className="mmtl_time">
                <FaClock className="mmtl_icon" />
                {meeting.meet_time 
                  ? new Date(meeting.meet_time).toLocaleTimeString('en-US', {
                      hour: '2-digit',
                      minute: '2-digit',
                    }) 
                  : 'Unknown time'}
              </span>
            </Typography>
          </div>
          
          <Typography variant="body2" className="mmtl_delete_warning">
            This action cannot be undone. All data associated with this meeting will be permanently deleted.
          </Typography>

          {/* Error message */}
          {error && (
            <div className="mmtl_error">
              <FaTimes />
              <span>{error}</span>
            </div>
          )}
        </div>
      </DialogContent>

      <DialogActions className="mmtl_dialog_actions mmtl_delete_actions">
        <Button 
          onClick={onClose} 
          className="mmtl_cancel_button"
          disabled={loading}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleDelete} 
          className="mmtl_delete_button"
          disabled={loading}
        >
          {loading ? (
            <>
              <CircularProgress size={16} color="inherit" />
              <span>Deleting...</span>
            </>
          ) : (
            <span>Delete Meeting</span>
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// # Meeting Actions Component
const MeetingActions = ({ meeting, onEdit, onDelete }) => {
  return (
    <div className="mmtl_meeting_actions">
      <IconButton 
        className="mmtl_edit_button" 
        onClick={() => onEdit(meeting)}
        title="Edit Meeting"
      >
        <FaEdit />
      </IconButton>
      <IconButton 
        className="mmtl_delete_button" 
        onClick={() => onDelete(meeting)}
        title="Delete Meeting"
      >
        <FaTrash />
      </IconButton>
    </div>
  );
};

export default MeetingActions;
